export default class BrowserStorage
{
    isSupported = false;

    supportedTypes = [
        'sessionStorage',
        'localStorage',
    ]

    /**
     * BrowserStorage class encapsulation
     * @param {string} key
     * @param {('sessionStorage' | 'localStorage')} type
     * @param {boolean} [throwErrors = false]
     */
    constructor(key, type = 'localStorage', throwErrors = false)
    {
        this.key = key;
        this.type = type;
        this.throwErrors = throwErrors;
        this.setStorageType();
        this.checkSupport();
    }

    /**
     * Check for storage support
     * @returns {Boolean}
     */
    checkSupport()
    {
        this.isSupported = !!typeof (Storage);

        if (!this.isSupported && this.throwErrors)
        {
            throw Error('Storage is not supported in current browser');
        }

        return this.isSupported;
    }

    /**
     * Sets correct storage type
     */
    setStorageType()
    {
        if (this.supportedTypes.includes(this.type))
        {
            this.storage = window[this.type];
        }
        else if (this.throwErrors)
        {
            throw Error(`Type ${this.type} is not supported. Supported types are: ${this.supportedTypes.join(', ')}`);
        }
    }

    /**
     * Execution wrapper for commands
     * @param {string} cmd
     * @param  {...any} args
     * @returns
     */
    async executeCmd(cmd, ...args)
    {
        await null; // mimic async
        try
        {
            return this.storage[cmd](this.key, ...args);
        }
        catch (e)
        {
            if (this.throwErrors)
            {
                throw e;
            }
        }
        return false;
    }

    /**
     * Sets item into storage
     * @param {any} payload
     * @returns {Promise<undefined>}
     */
    async setItem(payload)
    {
        return this.executeCmd('setItem', JSON.stringify(payload));
    }

    /**
     * Gets item from storage - null if doesn't exist
     * @returns {Promise<Any>}
     */
    async getItem()
    {
        return JSON.parse(await this.executeCmd('getItem'));
    }

    /**
     * Checks items existence in storage
     * @returns {Promise<boolean>}
     */
    async hasItem()
    {
        return await this.executeCmd('getItem') !== null;
    }

    /**
     * Removes item from storage
     * @returns {Promise<undefined>}
     */
    async removeItem()
    {
        return this.executeCmd('removeItem');
    }

    /**
     * Clears whole storage
     * @returns {Promise<undefined>}
     */
    async clear()
    {
        return this.executeCmd('clear');
    }

    /**
     * Sets error throwing behaviour
     * @param {boolean} val
     */
    setErrorThrowing(val)
    {
        this.throwErrors = val;
    }
}
